import React, { useState, useEffect } from 'react';
import { Save, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import apiRequest from '../services/api';

const SetupPage = () => {
  const navigate = useNavigate();
  const { t, changeLanguage } = useTranslation();
  const { success, error: showError } = useNotification();
  const [formData, setFormData] = useState({
    timezone: 'UTC',
    language: 'en',
    weight_unit: 'kgs',
    length_unit: 'cm',
    currency: 'USD',
    domain: '',
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [currencies, setCurrencies] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [errors, setErrors] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  // Load initial data and pre-fill form
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setInitializing(true);
        setFetchError(null);
        
        // 1. Fetch current system parameters for pre-fill
        try {
          const paramsData = await apiRequest('/api/settings/params');
          if (paramsData.status === 'success') {
            setFormData(prev => ({
              ...prev,
              timezone: paramsData.data.timezone || 'UTC',
              language: paramsData.data.language || 'en',
              weight_unit: paramsData.data.weight_unit || 'kgs',
              length_unit: paramsData.data.length_unit || 'cm',
              currency: paramsData.data.currency || 'USD',
              domain: paramsData.data.domain || '',
            }));
          }
        } catch (err) {
          // apiRequest already handles 401 redirect
          console.warn('Error fetching params:', err);
        }
        
        // 2. Load timezones for dropdown
        try {
          const timezonesData = await apiRequest('/api/settings/timezones');
          if (timezonesData.status === 'success') {
            setTimezones(timezonesData.data);
          }
        } catch (err) {
          // apiRequest already handles 401 redirect
          console.warn('Error fetching timezones:', err);
        }
        
        // 3. Load currencies for dropdown
        try {
          const currenciesData = await apiRequest('/api/settings/currencies');
          if (currenciesData.status === 'success') {
            setCurrencies(currenciesData.data);
          }
        } catch (err) {
          // apiRequest already handles 401 redirect
          console.warn('Error fetching currencies:', err);
        }
        
        // Check setup status
        try {
          const statusData = await apiRequest('/api/setup/status');
          if (statusData.setup_completed) {
            // If already completed, redirect to home
            navigate('/');
          }
        } catch (err) {
          // apiRequest already handles 401 redirect
          console.warn('Error fetching setup status:', err);
        }
        
      } catch (err) {
        console.error('Failed to load initial data:', err);
        setFetchError(t('settings.setup_load_failed') || 'Failed to load setup data. Please refresh the page.');
      } finally {
        setInitializing(false);
      }
    };
    
    loadInitialData();
  }, [navigate, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    // Validate form
    const validationErrors = [];
    if (!formData.domain.trim()) {
      validationErrors.push(t('settings.domain_required') || 'Domain is required');
    }
    if (!formData.timezone) {
      validationErrors.push(t('settings.timezone_required') || 'Timezone is required');
    }
    if (!formData.language) {
      validationErrors.push(t('settings.language_required') || 'Language is required');
    }
    if (!formData.weight_unit) {
      validationErrors.push(t('settings.weight_unit_required') || 'Weight unit is required');
    }
    if (!formData.length_unit) {
      validationErrors.push(t('settings.length_unit_required') || 'Length unit is required');
    }
    if (!formData.currency) {
      validationErrors.push(t('settings.currency_required') || 'Currency is required');
    }
    
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    setLoading(true);
    setErrors([]);
    
    try {
      // 1. Update parameters
      const paramsResult = await apiRequest('/api/setup/params', {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      
      if (paramsResult.status !== 'success') {
        throw new Error(paramsResult.message || t('settings.save_params_failed') || 'Failed to save parameters');
      }
      
      // 2. Mark setup as completed
      const completeResult = await apiRequest('/api/setup/complete', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      
      if (completeResult.status !== 'success') {
        throw new Error(completeResult.message || t('settings.mark_complete_failed') || 'Failed to mark setup as complete');
      }
      
      // Success! Update language and show notification
      if (formData.language) {
        changeLanguage(formData.language);
      }
      success(t('settings.setup_complete_success'));
      navigate('/');
      
    } catch (err) {
      console.error('Setup failed:', err);
      setErrors([err.message || t('settings.setup_failed')]);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="p-8 border-b border-slate-200 dark:border-slate-800 bg-blue-600 text-white">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle size={24} />
            <h1 className="text-2xl font-bold">{t('settings.setup_title')}</h1>
          </div>
          <p className="text-blue-100">{t('settings.setup_welcome')}</p>
        </div>

        {/* Error display */}
        {errors.length > 0 && (
          <div className="mx-8 -mt-4">
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="text-red-600 dark:text-red-400 mt-0.5" size={18} />
                <div className="space-y-1">
                  {errors.map((error, idx) => (
                    <p key={idx} className="text-sm text-red-700 dark:text-red-300">{error}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Fetch error display */}
        {fetchError && (
          <div className="mx-8 -mt-4">
            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="text-yellow-600 dark:text-yellow-400 mt-0.5" size={18} />
                <div>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">{fetchError}</p>
                  <button 
                    onClick={() => window.location.reload()}
                    className="mt-2 text-sm text-yellow-800 dark:text-yellow-200 underline hover:no-underline"
                  >
                    {t('common.refresh_page')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {initializing ? (
            // Loading skeleton
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
                  <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
                  <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded"></div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="space-y-2">
                    <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
                    <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded"></div>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
                <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded"></div>
              </div>
              <div className="pt-6">
                <div className="h-12 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>
              </div>
            </div>
          ) : (
            // Actual form
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('settings.timezone')}</label>
                  <select 
                    className="w-full p-2 border rounded-lg bg-transparent dark:border-slate-700"
                    value={formData.timezone}
                    onChange={e => setFormData({...formData, timezone: e.target.value})}
                    disabled={timezones.length === 0}
                  >
                    {timezones.length > 0 ? (
                      timezones.map(tz => (
                        <option key={tz} value={tz}>{tz}</option>
                      ))
                    ) : (
                      <option value={formData.timezone}>{formData.timezone}</option>
                    )}
                  </select>
                  {timezones.length === 0 && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('settings.loading_timezones')}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('settings.language')}</label>
                  <select 
                    className="w-full p-2 border rounded-lg bg-transparent dark:border-slate-700"
                    value={formData.language}
                    onChange={e => setFormData({...formData, language: e.target.value})}
                  >
                    <option value="en">English</option>
                    <option value="zh">{t('setup.chinese')}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('settings.weight_unit')}</label>
                  <select 
                    className="w-full p-2 border rounded-lg bg-transparent dark:border-slate-700"
                    value={formData.weight_unit}
                    onChange={e => setFormData({...formData, weight_unit: e.target.value})}
                  >
                    <option value="kgs">{t('settings.kgs_label')}</option>
                    <option value="lbs">{t('settings.lbs_label')}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('settings.length_unit')}</label>
                  <select 
                    className="w-full p-2 border rounded-lg bg-transparent dark:border-slate-700"
                    value={formData.length_unit}
                    onChange={e => setFormData({...formData, length_unit: e.target.value})}
                  >
                    <option value="cm">{t('settings.cm_label')}</option>
                    <option value="inch">{t('settings.inch_label')}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('settings.currency_unit')}</label>
                  <select 
                    className="w-full p-2 border rounded-lg bg-transparent dark:border-slate-700"
                    value={formData.currency}
                    onChange={e => setFormData({...formData, currency: e.target.value})}
                  >
                    {currencies.length > 0 ? (
                      currencies.map(currency => (
                        <option key={currency.code} value={currency.code}>
                          {currency.code} - {currency.name} ({currency.symbol})
                        </option>
                      ))
                    ) : (
                      <option value={formData.currency}>{formData.currency}</option>
                    )}
                  </select>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400">{t('settings.system_domain_confirmation')}</label>
                  <input 
                    className="w-full p-2 border rounded-lg bg-transparent dark:border-slate-700"
                    value={formData.domain}
                    onChange={e => setFormData({...formData, domain: e.target.value})}
                    placeholder="eg: www.eoato.com"
                    required
                  />
                </div>
              </div>

              <div className="pt-6">
                <button 
                  type="submit" 
                  disabled={isSubmitting || loading}
                  className={`w-full py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-lg ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {t('common.processing')}
                    </span>
                  ) : loading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <Save size={20} />
                  )}
                  {t('settings.complete_setup')}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default SetupPage;
